import {
  AimOutlined,
  ArrowLeftOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PieChartOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { Channel, GET_CHANNELS, GET_ME, Me } from '@frontend/data-access';
import {
  IdeaChannelCreate,
  IdeaChannelManage,
  PLATFORM_TYPES,
} from '@frontend/feature-idea-channel';
import { Button, Divider, Layout, Modal, Skeleton, Typography } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

const { Header, Sider } = Layout;

type ActionTypes = 'CREATE' | 'MANAGE';

function IdeaChannelSideMenu({ type }: { type: PLATFORM_TYPES }) {
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const { t } = useTranslation('admin');
  const [collapsed, setCollapsed] = useState(false);
  const router = useRouter();
  const { id } = router.query;
  const path = router.asPath;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState<ActionTypes>();
  const { data, loading } = useQuery<{
    channels: Channel[];
  }>(GET_CHANNELS);

  const permissionList = [
    'ADMIN&FULL',
    'CHANNEL_REVIEWER&FULL',
    'INTERNAL_CHALLENGE_MANAGEMENT&FULL',
  ];
  const isDashboardAccess = permissionList.some((item) =>
    meData?.me.permissions.includes(item)
  );

  const renderAction = () => {
    switch (actionType) {
      case 'CREATE':
        return (
          <IdeaChannelCreate onCancel={handleClose} onFinish={handleClose} />
        );
      case 'MANAGE':
        return (
          <IdeaChannelManage onCancel={handleClose} onFinish={handleClose} />
        );
      default:
        return <div />;
    }
  };

  const handleClose = () => {
    setActionType(undefined);
    setIsModalOpen(false);
  };

  const handleModal = (type: ActionTypes) => {
    setActionType(type);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (
      data?.channels &&
      data?.channels.length > 0 &&
      path === '/idea-hub' &&
      !id
    ) {
      router.push({
        pathname: '/idea-hub',
        query: {
          ...router.query,
          id: data?.channels[0].id,
        },
      });
    }
  }, [data?.channels, id, path, router]);

  const MenuItem = ({ data }: { data: Channel }) => {
    return (
      <div
        className={`group px-3 py-2 flex justify-between items-center gap-1 cursor-pointer hover:bg-gray-400 rounded-lg ${
          id === data.id && 'bg-gray-400'
        }`}
        onClick={() =>
          router.push({
            pathname: '/idea-hub',
            query: {
              id: data.id,
            },
          })
        }
      >
        <Typography.Text className="!text-gray-900 !text-sm !font-medium truncate">
          {data.title}
        </Typography.Text>
        <span
          className={`min-w-7 px-2 py-1 flex items-center justify-center bg-gray-400 rounded-full text-xs leading-none font-medium group-hover:bg-primary-600 group-hover:text-white ${
            id === data.id && 'bg-primary-600 text-white'
          }`}
        >
          {data.ideasCount}
        </span>
      </div>
    );
  };

  const ManagementMenuItem = ({
    icon,
    title,
    route,
    count,
  }: {
    icon: React.ReactNode;
    title: string;
    route: string;
    count?: number;
  }) => {
    const isActive = router.pathname.split('/').pop() === route;
    return (
      <div
        className={`group px-3 py-2 flex justify-between items-center cursor-pointer hover:bg-gray-400 rounded-lg ${
          isActive ? 'bg-gray-400 text-primary-600' : 'text-gray-900'
        }`}
        onClick={() => router.push(`/idea-hub/${route}`)}
      >
        <div className="flex items-center gap-3">
          {icon}
          <div className="text-sm font-medium">{title}</div>
        </div>
        {count !== undefined && (
          <span
            className={`min-w-7 px-2 py-1 flex items-center justify-center bg-gray-400 rounded-full text-xs leading-none font-medium group-hover:bg-primary-600 group-hover:text-white ${
              isActive && 'bg-primary-600 text-white'
            }`}
          >
            {count}
          </span>
        )}
      </div>
    );
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      width={256}
      collapsedWidth={64}
      style={{
        background: '#fff',
      }}
      className="border-r border-gray-400"
    >
      <Header
        style={{ paddingLeft: 12, paddingRight: 12, background: '#fff' }}
        className="border-b border-gray-400"
      >
        <div className="flex justify-between items-center">
          {!collapsed && (
            <Button
              type="link"
              className="!p-0"
              onClick={() =>
                router.push(type === 'ADMIN' ? '/' : '/innovation')
              }
            >
              <ArrowLeftOutlined />
              {t('back')}
            </Button>
          )}
          <div>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
            />
          </div>
        </div>
      </Header>
      {!collapsed && (
        <div className="">
          <div className="p-3">
            <div className="text-gray-700 text-xs">
              {t('sidebar_idea_management_menu')}
            </div>
          </div>
          <div className="px-[6px] space-y-1">
            {isDashboardAccess && (
              <ManagementMenuItem
                icon={<PieChartOutlined />}
                title={t('sidebar_idea_management_dashboard_view')}
                route="dashboard"
              />
            )}
            <ManagementMenuItem
              icon={<AimOutlined />}
              title={t('sidebar_idea_management_evaluate_ideas')}
              route="evaluate"
            />
          </div>
          <Divider />
          <div className="p-3 flex justify-between items-center">
            <div className="text-gray-700 text-xs">
              {t('sidebar_idea_management_idea_channel')}
            </div>
            {meData?.me.role === 'ADMIN' && (
              <div>
                <div
                  className="text-xs font-normal text-primary-600 cursor-pointer"
                  onClick={() => handleModal('MANAGE')}
                >
                  {t('sidebar_idea_management_manage')}
                </div>
              </div>
            )}
          </div>
          {loading ? (
            <div className="px-3 space-y-2">
              <Skeleton.Button active size="small" block />
              <Skeleton.Button active size="small" block />
              <Skeleton.Button active size="small" block />
            </div>
          ) : (
            <div className="px-[6px] space-y-1">
              <div className="space-y-1 overflow-auto max-h-[50vh]">
                {data?.channels.map((item, index) => (
                  <MenuItem key={index} data={item} />
                ))}
              </div>
              {meData?.me.role === 'ADMIN' && (
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() => handleModal('CREATE')}
                >
                  {t('sidebar_idea_management_add_new')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      <Modal
        title={actionType === 'CREATE' ? t('add_channel') : t('idea_channels')}
        open={isModalOpen}
        onCancel={handleClose}
        footer={null}
        width={1024}
        centered
      >
        {renderAction()}
      </Modal>
    </Sider>
  );
}

export default IdeaChannelSideMenu;
